import { INTL_LOCALES, toLanguage } from "./constants";

type DateInput = Date | number | string;

const toDate = (value: DateInput) =>
  value instanceof Date ? value : new Date(value);

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Number, money and date formatters for one language. Components should use
 * `useFormat()`, which re-renders on a language switch; this is for code
 * outside React.
 */
export const createFormatters = (
  language?: string,
  yesterday: (time: string) => string = (time) => `Yesterday ${time}`,
) => {
  const locale = INTL_LOCALES[toLanguage(language)];
  // English keeps its compact "12:32PM"; French reads "12 h 32".
  const time = (date: Date) =>
    locale.startsWith("fr")
      ? date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })
      : date
          .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
          .replace(" ", "");

  return {
    locale,
    /** CAD, e.g. `$1,234.56` / `1 234,56 $`. Em dash for nullish values. */
    money: (value?: number | null) =>
      value == null
        ? "—"
        : new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "CAD",
          }).format(value),
    number: (value: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, options).format(value),
    date: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
      toDate(value).toLocaleDateString(locale, options),
    dateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
      toDate(value).toLocaleString(locale, options),
    /** "12:32PM" today, "Yesterday 12:32PM", else "12 Oct 2025 8:09AM". */
    timestamp: (iso?: string | null) => {
      const date = iso ? new Date(iso) : null;
      if (!date || isNaN(date.getTime())) {
        return "";
      }
      const now = new Date();
      if (isSameDay(date, now)) {
        return time(date);
      }
      const previous = new Date(now);
      previous.setDate(now.getDate() - 1);
      if (isSameDay(date, previous)) {
        return yesterday(time(date));
      }
      const day = date.toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      return `${day} ${time(date)}`;
    },
  };
};

export type Formatters = ReturnType<typeof createFormatters>;
